import React, { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import AppShell from './AppShell';
import { CircularProgress } from '@mui/material';

interface ProtectedRouteProps {
    children: ReactNode;
    requiredPermission?: string;
    /**
     * Allow the screen without a staff member signed in. Only for the PIN screen
     * itself, which needs the property session but is what you see before anyone
     * has identified themselves.
     */
    allowLocked?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredPermission, allowLocked }) => {
    const { isAuthenticated, authChecked, staff, hasPerm, refreshPermissions } = useAuth();
    const location = useLocation();

    // Re-fetch permissions on every page navigation. Keyed on the staff member
    // too, since the answer changes entirely when the device changes hands.
    useEffect(() => {
        if (isAuthenticated && staff) {
            refreshPermissions();
        }
    }, [location.pathname, isAuthenticated, staff, refreshPermissions]);

    if (!authChecked) {
        return <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                zIndex: 99,
            }}
        >
            <CircularProgress style={{ zIndex: 99 }} />
            <div style={{ marginTop: '16px', color: '#000', fontSize: '18px' }}>
                Loading...
            </div>
        </div>;
    }

    if (isAuthenticated) {
        // The venue is signed in but nobody has taken the device yet, so send
        // them to the PIN screen rather than all the way back to the login form.
        if (!staff && !allowLocked) {
            return <Navigate to="/lock" replace />;
        }
        if (requiredPermission && !hasPerm(requiredPermission)) {
            return (
                <AppShell>
                    <div style={{ padding: 48, textAlign: 'center' }}>
                        <h2 style={{ color: '#101828', marginBottom: 8 }}>Access Denied</h2>
                        <p style={{ color: '#667085' }}>You don't have permission to access this page. Contact your administrator.</p>
                    </div>
                </AppShell>
            );
        }
        return <AppShell>{children}</AppShell>;
    } else {
        return <Navigate to="/login" replace />;
    }
};

export default ProtectedRoute;
